import { useLoginMutation, useLazyGetUserDetailsQuery } from 'util/rtk-api'
import { useDocTitle } from 'util/hooks'

import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { AuthCard, AuthDivider } from 'auth/auth-card'

export const Login = () => {
	const navigate = useNavigate()
	const [ login ] = useLoginMutation()
	const [ triggerGetUserDetails ] = useLazyGetUserDetailsQuery()

	const [ username, setUsername ] = useState()
	const [ password, setPassword ] = useState()
	const [ error, setError ] = useState(null)

	// Redirect to dashboard if already logged in
	useEffect(() => {
		const loggedIn = localStorage.getItem('loggedIn')
		const expiration = new Date(30 * 24 * 60 * 60 * 1000).getTime()
		if (loggedIn && Date.now() - loggedIn < expiration) {
			const isStudent = localStorage.getItem('studentId')
			navigate(isStudent ? '/enter-code' : '/dashboard', { replace: true })
		}
	}, [navigate])

	const onUsernameBlur = ({ target }) => {
		setUsername(target.value)
	}

	const onPasswordBlur = ({ target }) => {
		setPassword(target.value)
	}

	const onFormSubmit = async event => {
		event.preventDefault()

		const result = await login({ username, password })

		if ('error' in result) {
			const err = result.error

			// err-12: a network failure used to reject unhandled and do NOTHING
			if (err.status === 'FETCH_ERROR' || err.status === 'TIMEOUT_ERROR') {
				setError("We couldn't reach the server. Check your internet connection and try again.")
				return
			}

			// Handle Cognito challenges
			if (err.status === 401) {
				const body = err.data
				if (body?.challenge === 'NEW_PASSWORD_REQUIRED' && body?.session) {
					navigate(`/new-password-required?username=${encodeURIComponent(username)}&session=${encodeURIComponent(body.session)}`)
					return
				}
			}

			// err-12: the login throttle (429) and server errors used to show
			// "Incorrect email or password", inviting more attempts
			if (err.status === 429) {
				setError('Too many login attempts. Please wait a few minutes and try again.')
				return
			}
			if (typeof err.status === 'number' && err.status >= 500) {
				setError('Something went wrong on our side. Please try again in a moment.')
				return
			}

			setError("Incorrect email or password. Please try again.")
			return
		}

		localStorage.setItem('loggedIn', Date.now())
		// After login, check account type to route students to enter code
		try {
			const details = await triggerGetUserDetails().unwrap()
			if (details?.accountType === 'student') {
				// Persist student info for later use in student flows
				if (details?.userId) localStorage.setItem('studentId', details.userId)
				if (details?.firstName) localStorage.setItem('firstName', details.firstName)
				navigate('/enter-code')
				return
			}
			// Ensure studentId is cleared for non-student accounts
			localStorage.removeItem('studentId')
		} catch (e) {
			// fallback to dashboard on failure
			localStorage.removeItem('studentId')
		}
		navigate('/dashboard')
	}

	useDocTitle('All for One - Log in')

	return (
		<AuthCard narrow eyebrow="Welcome back" title="Hey there!" subtitle="Log in to keep designing lessons for every mind.">
			<form
				id="login-form"
				action="#"
				method="POST"
				onSubmit={onFormSubmit}
			>
				<div className="auth-field">
					{/* the design labels both fields above the box rather than relying on the placeholder */}
					<label htmlFor="username" className="auth-field-label">Username or email</label>
					<input
						type="text"
						id="username"
						name="username"
						placeholder="Username or email"
						required
						onChange={onUsernameBlur}
					/>
				</div>

				<div className="auth-field">
					<label htmlFor="password" className="auth-field-label">Password</label>
					<input
						type="password"
						id="password"
						name="password"
						placeholder="Password"
						required
						onChange={onPasswordBlur}
					/>
				</div>

				<div className="auth-forgot-row">
					<Link to="/forgot-password">Forgot password?</Link>
				</div>

				{error && <p className="auth-error">{error}</p>}

				<button type="submit" className="auth-btn">Log in</button>
			</form>

			<AuthDivider />

			<p className="auth-prompt">
				Don't have an account? <Link to="/signup" className="auth-link">Sign up!</Link>
			</p>
		</AuthCard>
	)
}
